import { useCallback, useMemo } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete02Icon, PlusSignIcon } from '@hugeicons/core-solid-rounded'
import { Trans, useLingui } from '@lingui/react/macro'
import { GraphQL } from '@guestgoodie/graphql'
import { useFieldArray } from 'react-hook-form'

import * as Form from '@/features/form'
import Input from '@/components/ui/Input'
import Dropdown from '@/components/ui/Dropdown'
import Textarea from '@/components/ui/Textarea'
import { FormData } from '../hooks/useGoodieForm'

function RequestFieldInput({
  index,
  name,
  remove
}: {
  index: number
  name: string
  remove: () => void
}) {
  const { t } = useLingui()

  const typeOptions = useMemo(() => {
    return [
      {
        value: GraphQL.RequestFieldType.Text,
        label: t`Text`
      },
      {
        value: GraphQL.RequestFieldType.Textarea,
        label: t`Textarea`
      },
      {
        value: GraphQL.RequestFieldType.Number,
        label: t`Number`
      },
      {
        value: GraphQL.RequestFieldType.Checkbox,
        label: t`Checkbox`
      },
      {
        value: GraphQL.RequestFieldType.File,
        label: t`File`
      },
      {
        value: GraphQL.RequestFieldType.Dropdown,
        label: t`Dropdown`
      }
    ]
  }, [t])

  return (
    <div className="flex flex-row gap-2 w-full bg-primary/10 rounded-small p-2.5 items-start">
      <div className="flex flex-col gap-1 flex-1">
        <div className="flex flex-row items-start gap-2 justify-evenly">
          <div className="flex-1">
            <Form.Field name={`${name}.type`} label={t`Type`}>
              {({ renderProps }) => (
                <Dropdown options={typeOptions} {...renderProps} />
              )}
            </Form.Field>
          </div>
          <div className="flex-1">
            <Form.Field
              name={`${name}.label`}
              label={t`Label`}
              rules={{
                maxLength: {
                  value: 255,
                  message: t`Can't be longer than 255 characters.`
                },
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  type="text"
                  {...props}
                  placeholder={t`Label`}
                  onChange={event => onChange(event.target.value)}
                />
              )}
            </Form.Field>
          </div>
        </div>
        <Form.Field name={`${name}.description`} label={t`Description`}>
          {({ renderProps: { onChange, value, ...props } }) => (
            <Textarea
              {...props}
              rows={2}
              value={value ?? ''}
              placeholder={t`Description`}
              onChange={event => onChange(event.target.value)}
            />
          )}
        </Form.Field>
        <Form.Data<FormData>>
          {({ default: { requestFields } }) => {
            const field = requestFields?.[index]

            if (!field || field.type !== GraphQL.RequestFieldType.Dropdown)
              return null

            return <span>TODO</span>
          }}
        </Form.Data>
      </div>
      <button type="button" onClick={remove}>
        <HugeiconsIcon icon={Delete02Icon} className="size-5 text-red-500" />
      </button>
    </div>
  )
}

export function RequestFieldsInput({ name }: { name: string }) {
  const { fields, append, remove } = useFieldArray({
    name,
    shouldUnregister: true
  })

  const onAdd = useCallback(() => {
    append({
      type: GraphQL.RequestFieldType.Text,
      label: ''
    })
  }, [append])

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2.5">
      <span className="text-text text-heading3">
        <Trans>Request Fields</Trans>
      </span>
      <span className="text-regular text-text">
        <Trans>
          Here you can add information you want to collect from guests when they
          book this Goodie.
        </Trans>
      </span>
      <div className="flex flex-col gap-4">
        {fields?.map((field, index) => {
          return (
            <RequestFieldInput
              key={field.id}
              name={`${name}.${index}`}
              index={index}
              remove={() => remove(index)}
            />
          )
        })}
        <button
          onClick={onAdd}
          type="button"
          className="flex flex-row justify-center items-center p-2 border border-dashed border-text-light rounded-small gap-1"
        >
          <HugeiconsIcon
            icon={PlusSignIcon}
            className="size-5 text-text-light"
          />
          <span className="text-text-light text-regular">
            <Trans>Add Field</Trans>
          </span>
        </button>
      </div>
    </div>
  )
}
